import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { apiClient } from '@/lib/api-client';
import { useBatchContext } from '@/app/BatchContext';
import { PageHeader } from '@/components/efs/PageHeader';
import { ListPagination } from '@/components/efs/ListPagination';
import { BatchStatusBadge, type BatchStatus } from '@/components/efs/BatchStatusBadge';
import { CreateBatchDialog } from './CreateBatchDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatBatchFacility, formatBatchMonth } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { canAccessPath } from '@/lib/role-access';
import {
  AsyncStateBoundary,
  BLOCKER_CATEGORY_SECTIONS,
  blockerCategoryKeys,
  isBlockerCategory,
  resolveIssueNavigation,
} from '@/features/audit-issues';
import { fetchBlockerBatches } from '@/features/dashboard/api';
import type { BlockerBatch } from '@/features/dashboard/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MonthlyBatchDto {
  monthly_batch_id: string;
  facility_id: string;
  facility_name: string;
  facility_role: string;
  company_display_name: string;
  company_party_type: string;
  year_month: string;
  display_code: string;
  status: string;
  closed_at: string | null;
  package_generated_at: string | null;
}

interface BatchPurgeDependencyCount {
  table_name: string;
  count: number;
}

interface BatchPurgePreviewDto {
  monthly_batch_id: string;
  dependencies: BatchPurgeDependencyCount[];
  total_dependents: number;
  requires_confirmation: boolean;
  retained_reports?: Array<{
    artifact_id: string;
    variant: string;
    version: number;
    size_bytes: number | null;
  }>;
  retained_report_bytes?: number;
}

interface MonthlyBatchListMeta {
  total: number;
  page: number;
  per_page: number;
}

interface MonthlyBatchListResult {
  data: MonthlyBatchDto[];
  meta: MonthlyBatchListMeta;
}

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const MonthlyBatchesPage = () => {
  const { t } = useTranslation();
  const { batchId, setBatchId } = useBatchContext();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MonthlyBatchDto | null>(null);
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeConfirmation, setPurgeConfirmation] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const canPurge = hasPermission(currentUser, 'system.admin');
  // Round-6 item 7: `?blocker=<category>` is a REAL filter — the page fetches
  // the scoped per-category batch list instead of the unfiltered list.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const blockerParam = searchParams.get('blocker');
  const blockerCategory = blockerParam && isBlockerCategory(blockerParam) ? blockerParam : null;
  const blockerList = useQuery({
    queryKey: ['dashboard', 'blocker-batches', blockerCategory, { page, per_page: perPage }],
    queryFn: () => fetchBlockerBatches(blockerCategory!, { page, per_page: perPage }),
    enabled: !!blockerCategory,
  });
  const { data, isLoading } = useQuery<MonthlyBatchListResult>({
    queryKey: ['monthly-batches', 'list', { page, per_page: perPage }],
    enabled: !blockerCategory,
    queryFn: async () => {
      const res = await apiClient.get<{ data: MonthlyBatchDto[]; meta?: MonthlyBatchListMeta }>(
        '/monthly-batches',
        { params: { page, per_page: perPage } },
      );
      const rows = res.data.data ?? [];
      return {
        data: rows,
        meta: res.data.meta ?? { total: rows.length, page, per_page: perPage },
      };
    },
  });
  const deleteBatch = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/monthly-batches/${id}`);
    },
    onSuccess: async (_, id) => {
      if (batchId === id) setBatchId(null);
      setDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['monthly-batches'] });
    },
  });
  const purgePreview = useMutation({
    mutationFn: async (id: string): Promise<BatchPurgePreviewDto> => {
      const res = await apiClient.post<{ data: BatchPurgePreviewDto }>(
        `/monthly-batches/${id}/purge-preview`,
        {},
      );
      return res.data.data;
    },
  });
  const purgeBatch = useMutation({
    mutationFn: async ({
      id,
      reason,
      confirmation,
    }: {
      id: string;
      reason: string;
      confirmation: string;
    }) => {
      await apiClient.post(`/monthly-batches/${id}/purge`, { reason, confirmation });
    },
    onSuccess: async (_, { id }) => {
      if (batchId === id) setBatchId(null);
      setDeleteTarget(null);
      setPurgeReason('');
      setPurgeConfirmation('');
      await queryClient.invalidateQueries({ queryKey: ['monthly-batches'] });
    },
  });
  const batches = data?.data ?? [];
  const total = blockerCategory ? (blockerList.data?.meta.total ?? 0) : (data?.meta.total ?? 0);

  const changePerPage = (nextPerPage: number) => {
    setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
    setPage(1);
  };

  const openDeleteDialog = (batch: MonthlyBatchDto) => {
    setDeleteTarget(batch);
    setPurgeReason('');
    setPurgeConfirmation('');
    purgeBatch.reset();
    purgePreview.reset();
    deleteBatch.reset();
    if (canPurge) purgePreview.mutate(batch.monthly_batch_id);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.monthly-batches.list.title', { ns: 'batches', defaultValue: '月合計表' })}
        subtitle={t('efs.monthly-batches.list.subtitle', {
          ns: 'batches',
          defaultValue:
            '月合計表 = 1 施設 × 1 ヶ月の集計単位。入荷・仕分け・解体・出荷の全記録がこの表に紐づきます。',
        })}
        count={total}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t('efs.batches.create.button', {
              ns: 'batches',
              defaultValue: '月合計表を作成',
            })}
          </Button>
        }
      />
      <CreateBatchDialog open={createOpen} onOpenChange={setCreateOpen} navigateAfterCreate />
      {blockerCategory ? (
        <div className="space-y-3" data-testid="blocker-filter">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full border border-efs-border bg-efs-bg-subtle px-3 py-1">
              {t('efs.monthly-batches.blocker_filter.label', { ns: 'batches' })}:{' '}
              <span className="font-medium">
                {t(blockerCategoryKeys(blockerCategory).title, { ns: 'issues' })}
              </span>
            </span>
            <Link to="/batches" className="text-xs text-efs-accent hover:underline">
              {t('efs.monthly-batches.blocker_filter.clear', { ns: 'batches' })}
            </Link>
          </div>
          <AsyncStateBoundary
            isLoading={blockerList.isLoading}
            isError={blockerList.isError}
            error={blockerList.error}
            data={blockerList.data}
            isEmpty={(d) => d.data.length === 0}
            onRetry={() => void blockerList.refetch()}
            emptyContent={
              <p className="text-efs-text-muted">
                {t('efs.monthly-batches.blocker_filter.empty', { ns: 'batches' })}
              </p>
            }
          >
            {(d) => (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.monthly-batches.fields.year_month', { ns: 'batches' })}
                    </TableHead>
                    <TableHead>
                      {t('efs.monthly-batches.fields.facility', { ns: 'batches' })}
                    </TableHead>
                    <TableHead>
                      {t('efs.monthly-batches.fields.status', { ns: 'batches' })}
                    </TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.data.map((b: BlockerBatch) => {
                    const section = BLOCKER_CATEGORY_SECTIONS[blockerCategory];
                    const resolved = resolveIssueNavigation(
                      {
                        kind: 'monthly_batch',
                        params: { batch_id: b.monthly_batch_id, section: section ?? undefined },
                      },
                      b.monthly_batch_id,
                    );
                    const path = resolved.ok ? resolved.path : (resolved.fallbackPath ?? null);
                    const allowed = !!path && canAccessPath(currentUser, path.split('?')[0]);
                    return (
                      <TableRow key={b.monthly_batch_id}>
                        <TableCell>{formatBatchMonth(b.year_month)}</TableCell>
                        <TableCell>{b.facility_name ?? '—'}</TableCell>
                        <TableCell>
                          <BatchStatusBadge status={b.status as BatchStatus} />
                        </TableCell>
                        <TableCell className="text-right">
                          {allowed && path ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setBatchId(b.monthly_batch_id);
                                navigate(path);
                              }}
                            >
                              {t(blockerCategoryKeys(blockerCategory).action, { ns: 'issues' })}
                            </Button>
                          ) : (
                            <span className="text-xs text-efs-text-muted">
                              {t('efs.issues.target.no_permission', { ns: 'issues' })}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </AsyncStateBoundary>
        </div>
      ) : isLoading ? (
        <p className="text-efs-text-muted">{t('efs.common.messages.loading')}</p>
      ) : batches.length === 0 ? (
        <p className="text-efs-text-muted">{t('efs.common.messages.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t('efs.monthly-batches.fields.year_month', {
                  ns: 'batches',
                  defaultValue: 'Year-Month',
                })}
              </TableHead>
              <TableHead>
                {t('efs.monthly-batches.fields.facility', {
                  ns: 'batches',
                  defaultValue: 'Facility',
                })}
              </TableHead>
              <TableHead>
                {t('efs.monthly-batches.fields.status', { ns: 'batches', defaultValue: 'Status' })}
              </TableHead>
              <TableHead>
                {t('efs.monthly-batches.fields.closed_at', {
                  ns: 'batches',
                  defaultValue: 'Closed',
                })}
              </TableHead>
              <TableHead>
                {t('efs.monthly-batches.fields.package_at', {
                  ns: 'batches',
                  defaultValue: 'Package',
                })}
              </TableHead>
              <TableHead aria-label="actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((b) => (
              <TableRow key={b.monthly_batch_id}>
                <TableCell>
                  {formatBatchMonth(b.year_month)}
                  {b.display_code ? (
                    <div className="font-mono text-xs text-efs-text-muted">{b.display_code}</div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="font-medium text-efs-text-primary">{formatBatchFacility(b)}</div>
                  <div className="text-xs text-efs-text-muted">
                    {b.facility_role} · {b.facility_id.slice(0, 8)}
                  </div>
                </TableCell>
                <TableCell>
                  <BatchStatusBadge status={b.status as BatchStatus} />
                </TableCell>
                <TableCell>{b.closed_at ? new Date(b.closed_at).toLocaleString() : '—'}</TableCell>
                <TableCell>
                  {b.package_generated_at ? new Date(b.package_generated_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {b.status === 'draft' || canPurge ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(b)}
                        title={t('efs.monthly-batches.actions.delete_empty_draft', {
                          ns: 'batches',
                        })}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        {canPurge && b.status !== 'draft'
                          ? t('efs.monthly-batches.actions.purge', {
                              ns: 'batches',
                              defaultValue: '強制削除',
                            })
                          : t('efs.monthly-batches.actions.delete', {
                              ns: 'batches',
                              defaultValue: 'Delete',
                            })}
                      </Button>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      onClick={() => setBatchId(b.monthly_batch_id)}
                    >
                      <Link to={`/batches/${b.monthly_batch_id}`}>
                        {t('efs.common.actions.open', { defaultValue: 'Open' })}
                      </Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ListPagination
        page={data?.meta.page ?? page}
        perPage={data?.meta.per_page ?? perPage}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPerPageChange={changePerPage}
      />
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
          if (!open) {
            setPurgeReason('');
            setPurgeConfirmation('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('efs.monthly-batches.delete.title', {
                ns: 'batches',
                defaultValue: '月合計表を削除',
              })}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? t('efs.monthly-batches.delete.description', {
                    ns: 'batches',
                    month: formatBatchMonth(deleteTarget.year_month),
                    facility: formatBatchFacility(deleteTarget),
                    defaultValue:
                      'Delete {{month}} / {{facility}}. Only empty draft batches can be deleted.',
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-100">
            {t('efs.monthly-batches.delete.guard', {
              ns: 'batches',
              defaultValue:
                'If this monthly summary sheet already has inbound, dispatch, evidence, inventory, report, or audit records, deletion will be rejected.',
            })}
          </p>
          {deleteBatch.error ? (
            <p className="text-xs text-red-900">{deleteBatch.error.message}</p>
          ) : null}

          {canPurge && deleteTarget ? (
            <div className="space-y-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-950 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
              <div className="font-semibold">
                {t('efs.monthly-batches.purge.title', {
                  ns: 'batches',
                  defaultValue: '管理員強制刪除',
                })}
              </div>
              <p className="text-xs">
                {t('efs.monthly-batches.purge.warning', {
                  ns: 'batches',
                  defaultValue:
                    'This removes the monthly summary sheet and its operational records. Type the full monthly summary sheet ID and provide a reason.',
                })}
              </p>
              {purgePreview.isPending ? (
                <p className="text-xs">
                  {t('efs.common.messages.loading', { defaultValue: 'Loading...' })}
                </p>
              ) : purgePreview.data ? (
                <div className="max-h-36 overflow-y-auto rounded border border-red-200 bg-white/80 p-2 dark:border-red-800 dark:bg-black/20">
                  {purgePreview.data.dependencies.length === 0 ? (
                    <p className="text-xs">
                      {t('efs.monthly-batches.purge.no_dependencies', {
                        ns: 'batches',
                        defaultValue: 'No dependent records.',
                      })}
                    </p>
                  ) : (
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      {purgePreview.data.dependencies.map((dep) => (
                        <div key={dep.table_name} className="contents">
                          <dt className="font-mono">{dep.table_name}</dt>
                          <dd className="text-right font-mono">{dep.count}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              ) : null}
              {purgePreview.error ? (
                <p className="text-xs text-red-900 dark:text-red-100">
                  {purgePreview.error.message}
                </p>
              ) : null}
              {purgePreview.data?.dependencies.some(
                (dependency) =>
                  dependency.count > 0 &&
                  ['monthly_report_artifacts', 'monthly_report_commercial_term_sets'].includes(
                    dependency.table_name,
                  ),
              ) ? (
                <div role="alert" className="space-y-2 rounded border border-red-300 p-3 text-sm">
                  <p>{t('efs.monthly-batches.purge.report_retention', { ns: 'batches' })}</p>
                  <p>
                    {t('efs.monthly-batches.purge.report_retention_size', {
                      ns: 'batches',
                      count: purgePreview.data.retained_reports?.length ?? 0,
                      bytes: new Intl.NumberFormat().format(
                        purgePreview.data.retained_report_bytes ?? 0,
                      ),
                    })}
                  </p>
                  <Link
                    className="underline"
                    to={`/batches/${deleteTarget.monthly_batch_id}/reports`}
                  >
                    {t('efs.monthly-batches.purge.open_reports', { ns: 'batches' })}
                  </Link>
                </div>
              ) : null}
              <div className="space-y-2">
                <label className="text-xs font-medium" htmlFor="monthly-batch-purge-reason">
                  {t('efs.monthly-batches.purge.reason', {
                    ns: 'batches',
                    defaultValue: '強制刪除原因',
                  })}
                </label>
                <Textarea
                  id="monthly-batch-purge-reason"
                  rows={3}
                  value={purgeReason}
                  onChange={(e) => setPurgeReason(e.target.value)}
                  placeholder={t('efs.monthly-batches.purge.reason_placeholder', {
                    ns: 'batches',
                    defaultValue: '例: 封測誤建月合計表，已確認不需保留作業資料',
                  })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium" htmlFor="monthly-batch-purge-confirmation">
                  {t('efs.monthly-batches.purge.confirmation', {
                    ns: 'batches',
                    id: deleteTarget.monthly_batch_id,
                    defaultValue: 'Type monthly summary sheet ID: {{id}}',
                  })}
                </label>
                <Input
                  id="monthly-batch-purge-confirmation"
                  value={purgeConfirmation}
                  onChange={(e) => setPurgeConfirmation(e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
              {purgeBatch.error ? (
                <p className="text-xs text-red-900 dark:text-red-100">{purgeBatch.error.message}</p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteBatch.isPending}
            >
              {t('efs.common.actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            {deleteTarget?.status === 'draft' ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (deleteTarget) deleteBatch.mutate(deleteTarget.monthly_batch_id);
                }}
                disabled={deleteBatch.isPending || purgeBatch.isPending}
              >
                {deleteBatch.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.monthly-batches.delete.confirm', {
                  ns: 'batches',
                  defaultValue: 'Delete empty draft',
                })}
              </Button>
            ) : null}
            {canPurge && deleteTarget ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() =>
                  purgeBatch.mutate({
                    id: deleteTarget.monthly_batch_id,
                    reason: purgeReason.trim(),
                    confirmation: purgeConfirmation.trim(),
                  })
                }
                disabled={
                  purgeBatch.isPending ||
                  deleteBatch.isPending ||
                  purgePreview.isPending ||
                  !!purgePreview.error ||
                  !purgePreview.data ||
                  purgePreview.data.dependencies.some(
                    (dependency) =>
                      dependency.count > 0 &&
                      ['monthly_report_artifacts', 'monthly_report_commercial_term_sets'].includes(
                        dependency.table_name,
                      ),
                  ) ||
                  purgeReason.trim().length < 10 ||
                  purgeConfirmation.trim() !== deleteTarget.monthly_batch_id
                }
              >
                {purgeBatch.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.monthly-batches.purge.confirm', {
                  ns: 'batches',
                  defaultValue: '強制刪除',
                })}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyBatchesPage;
